import { NavigateFunction } from 'react-router-dom';
import ROUTER_CONSTANT from '../constants/RouterConstant';

/**
 * In-app "go back" helpers.
 *
 * Back arrows must pop the history stack, not replace the current entry with a hardcoded page:
 * replacing leaves duplicate entries behind (Home → feature → back arrow → Home, then the iOS
 * swipe-back shows Home again) and ignores where the user actually came from.
 *
 * The hardcoded route is only a fallback for when there is no in-app history to go back to
 * (deep link / push-notification entry), in which case we replace so the dead-end page is gone.
 */

// react-router's BrowserRouter keeps `idx` (0 for the first entry of this app session) in
// window.history.state.
export const canGoBackInApp = (): boolean => {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === 'number' && idx > 0;
};

// Marks a POP as initiated by our own back control, so RouteTransition can tell it apart from a
// native iOS edge-swipe (which has already animated and must not be animated again).
const IN_APP_BACK_WINDOW_MS = 1000;
let inAppBackAt = 0;

export const markInAppBack = (): void => { inAppBackAt = Date.now(); };
export const wasInAppBack = (): boolean =>
  inAppBackAt > 0 && Date.now() - inAppBackAt <= IN_APP_BACK_WINDOW_MS;
export const consumeInAppBack = (): void => { inAppBackAt = 0; };

export const goBack = (navigate: NavigateFunction, fallback: string = ROUTER_CONSTANT.ROOT.HOME): void => {
  // history.go(-1) is async and idx is read synchronously: a double tap would pop two entries.
  // The mark is cleared once RouteTransition handles the POP.
  if (wasInAppBack()) return;
  if (canGoBackInApp()) {
    markInAppBack();
    navigate(-1);
  } else {
    navigate(fallback, { replace: true });
  }
};
